import { faArrowUpRightFromSquare, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverContent, PopoverTrigger } from '@plaidev/sour-react';
import { type ReactNode, useRef, useState } from 'react';
import QantLogomarkSvg from '../assets/images/brand/b-qant-logomark.svg?react';
import QantLogotypeSvg from '../assets/images/brand/b-qant-logotype.svg?react';
import { qantProductDefs, qantProductList } from '../config/qantProducts.js';
import type { LocalizedText } from '../config/type.js';
import type { MenuItem, ProductMenu } from '../types/menuModel.js';
import { AlternateEnvironmentLink } from './AlternateEnvironmentLink.js';
import { L1Icon } from './icons.js';
import { MenuGroupList } from './MenuGroupList.js';

type Locale = 'ja' | 'en';

const tx = (text: LocalizedText, locale: Locale): string => text[locale];

/** Figma `6226:3335` — ヘッダー px-8、カラム px-16、行 p-8 / 見出し pl-8 */
const COLUMN_PX = 'px-4';
const SECTION_LABEL_CLASS = 'pb-1 pl-2 pr-1 pt-2';
const PRODUCT_ROW_PADDING = 'p-2';

/** Figma `QANT_logo_h` — w-147px × h-64px、symbol / logotype を inset 指定で配置 */
function QantLogoHeader({ invisible = false }: { invisible?: boolean }): ReactNode {
  return (
    <div
      className={['flex h-14 shrink-0 items-start px-2', invisible ? 'invisible' : ''].filter(Boolean).join(' ')}
      aria-hidden={invisible ? true : undefined}
    >
      <div className="relative h-16 w-[147px] shrink-0 overflow-hidden">
        <div className="absolute inset-[24.77%_66.79%_24.79%_10.88%]">
          <QantLogomarkSvg aria-hidden className="block size-full" />
        </div>
        <div className="absolute inset-[35.9%_10.88%_35.93%_37.71%]">
          <QantLogotypeSvg aria-label={invisible ? undefined : 'QANT'} className="block size-full" />
        </div>
      </div>
    </div>
  );
}

const PRODUCT_SECTIONS: readonly {
  label: LocalizedText;
  productNames: readonly string[];
}[] = [
  {
    label: { ja: 'プロダクト', en: 'Products' },
    productNames: Object.values(qantProductDefs.PRODUCTS).map((def) => def.product.name),
  },
  {
    label: { ja: '共通機能・基盤', en: 'Common Platform' },
    productNames: Object.values(qantProductDefs.COMMON).map((def) => def.product.name),
  },
  {
    label: { ja: '連携プロダクト', en: 'Linked Products' },
    productNames: Object.values(qantProductDefs.LINKED).map((def) => def.product.name),
  },
];

export type ProductSwitcherPopoverProps = {
  products?: readonly ProductMenu[];
  currentProductName: string;
  trigger: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSelectProduct?: (product: ProductMenu) => void;
  hrefResolver?: (menu: MenuItem) => string;
  /**
   * 通常プロダクト行 (<a href>) の遷移先 (ランディング href) を解決する。指定すると各行が
   * 実リンクになり、⌘/Ctrl・中クリックでの別タブ遷移がブラウザ標準で効くようになる。
   * 未指定時は従来通り onSelectProduct のみで遷移する。
   */
  resolveProductHref?: (product: ProductMenu) => string | undefined;
  onMenuClick?: (menu: MenuItem, event: React.MouseEvent<HTMLAnchorElement>) => void;
  locale?: Locale;
};

/**
 * プロダクト切り替えポップオーバー。
 *
 * 2 カラム構成:
 *   - 左: プロダクト一覧 (グループ見出し付き)
 *   - 右: ホバー / 選択中プロダクトのメニュープレビュー
 */
export function ProductSwitcherPopover({
  products = qantProductList,
  currentProductName,
  trigger,
  open,
  onOpenChange,
  onSelectProduct,
  hrefResolver,
  resolveProductHref,
  onMenuClick,
  locale = 'ja',
}: ProductSwitcherPopoverProps): ReactNode {
  const [hoveredName, setHoveredName] = useState<string | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement>(null);
  const productByName = new Map(products.map((p) => [p.name, p]));
  const activeName = hoveredName ?? currentProductName;
  const previewProduct = productByName.get(activeName);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {/*
        ポップオーバーはトリガー (プロダクト切替ボタン) を基準に右側へ出す。トリガーは展開時
        サイドバー幅一杯 (右端 ≒ 215px) なので sideOffset=16 で左上がサイドバー右端 (224px) +
        約 8px の位置に来る。
        NOTE: 以前は nav ボックスを PopoverAnchor (absolute inset-0) にしていたが、その anchor は
        floating-ui の測位参照として機能せず原点 (0,0) 基準になり、ポップオーバーがサイドバーに
        重なる不具合があった (z-index ではなく位置のリグレッション)。トリガー基準に戻して解消。
      */}
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        side="right"
        align="start"
        sideOffset={16}
        // Radix (FocusScope) はマウント時のオートフォーカス候補から <a> を除外するため、リンク行
        // 主体の本ポップオーバーではデフォルトだと唯一の <button> (右カラム末尾のポップオーバー
        // メニュー行) にフォーカスが飛ぶ。先頭のプロダクト行へ明示的にフォーカスする。
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.querySelector<HTMLElement>('a, button')?.focus();
        }}
        className="z-50 flex h-[640px] w-[640px] overflow-hidden rounded-xl border border-muted p-0"
      >
        <div className="flex h-full w-[320px] shrink-0 flex-col bg-default">
          <QantLogoHeader />
          <div className={`flex-1 overflow-y-auto pb-4 ${COLUMN_PX}`}>
            {PRODUCT_SECTIONS.map((section) => {
              const items = section.productNames.map((name) => productByName.get(name)).filter((p): p is ProductMenu => Boolean(p));
              if (items.length === 0) return null;
              return (
                <div key={section.label.ja} className="mb-2">
                  <p className={`text-xs font-semibold text-muted ${SECTION_LABEL_CLASS}`}>{tx(section.label, locale)}</p>
                  <ul className="flex flex-col gap-y-0.5">
                    {items.map((product) => (
                      <li key={product.name}>
                        <ProductRow
                          product={product}
                          isSelected={activeName === product.name}
                          locale={locale}
                          hrefResolver={hrefResolver}
                          resolveProductHref={resolveProductHref}
                          onPointerEnter={() => {
                            // 外部リンクプロダクトはプレビュー (右カラム) を持たないのでホバーで切り替えない
                            if (!product.link) setHoveredName(product.name);
                          }}
                          onSelect={() => {
                            onSelectProduct?.(product);
                            onOpenChange?.(false);
                          }}
                          onLinkClick={() => onOpenChange?.(false)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex h-full w-[320px] flex-col bg-default">
          {previewProduct && !previewProduct.link ? (
            <>
              <QantLogoHeader invisible />
              <div className={`flex-1 overflow-y-auto pb-4 ${COLUMN_PX}`}>
                <p className={`text-xs font-semibold text-muted ${SECTION_LABEL_CLASS}`}>
                  {previewProduct.menuGroups[0]?.label ? tx(previewProduct.menuGroups[0].label, locale) : tx(previewProduct.label, locale)}
                </p>
                <MenuGroupList
                  groups={previewProduct.menuGroups}
                  hrefResolver={hrefResolver}
                  onMenuClick={(menu, event) => {
                    onMenuClick?.(menu, event);
                    onOpenChange?.(false);
                  }}
                  locale={locale}
                  hideFirstGroupLabel
                />
                {previewProduct.alternateEnvironmentLink && hrefResolver ? (
                  <AlternateEnvironmentLink
                    link={previewProduct.alternateEnvironmentLink}
                    hrefResolver={hrefResolver}
                    onMenuClick={(menu, event) => {
                      onMenuClick?.(menu, event);
                      onOpenChange?.(false);
                    }}
                    locale={locale}
                  />
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ProductRow({
  product,
  isSelected,
  locale,
  hrefResolver,
  resolveProductHref,
  onPointerEnter,
  onSelect,
  onLinkClick,
}: {
  product: ProductMenu;
  isSelected: boolean;
  locale: Locale;
  hrefResolver?: (menu: MenuItem) => string;
  resolveProductHref?: (product: ProductMenu) => string | undefined;
  onPointerEnter: () => void;
  onSelect: () => void;
  onLinkClick: () => void;
}): ReactNode {
  const className = [
    'group flex w-full cursor-pointer items-center gap-2 rounded-md text-left transition-colors',
    PRODUCT_ROW_PADDING,
    isSelected ? 'bg-action-subtle-hover' : 'hover:bg-action-subtle-hover',
  ].join(' ');

  const body = (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-action-subtle text-default shadow-1">
        <L1Icon icon={product.icon} className="size-[18px]" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold text-primitive-black">{tx(product.label, locale)}</span>
        <span className="truncate text-xs text-muted">{tx(product.description, locale)}</span>
      </span>
    </>
  );

  // 外部リンクプロダクト (KARTE 本体等): サブメニューを持たず、クリックで直接遷移する。
  // openAnotherTab 指定時は新規タブで開く (target=_blank)。
  if (product.link) {
    const linkMenu: MenuItem = { name: product.name, label: product.label, path: product.link.path, options: product.link.options };
    const href = hrefResolver ? hrefResolver(linkMenu) : (product.link.path.path ?? '#');
    const openAnotherTab = product.link.options?.openAnotherTab === true;
    return (
      <a
        href={href}
        target={openAnotherTab ? '_blank' : undefined}
        rel={openAnotherTab ? 'noopener noreferrer' : undefined}
        onPointerEnter={onPointerEnter}
        onFocus={onPointerEnter}
        onClick={onLinkClick}
        className={className}
      >
        {body}
        {openAnotherTab ? (
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="shrink-0 text-[10px] text-muted" aria-label="新しいタブで開く" />
        ) : null}
      </a>
    );
  }

  // 通常プロダクト: ⌘/Ctrl・中クリックで別タブ遷移できるよう <a href> で描画する。
  // 通常クリックは onSelect（SPA / フルナビゲーション）に委ね、修飾キー/中クリックのみ
  // ブラウザ標準の別タブ遷移に任せる。href が解決できない場合は従来の <button> にフォールバック。
  const productHref = resolveProductHref?.(product);
  if (productHref) {
    return (
      <a
        href={productHref}
        onPointerEnter={onPointerEnter}
        onFocus={onPointerEnter}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
          event.preventDefault();
          onSelect();
        }}
        className={className}
      >
        {body}
        {isSelected ? <FontAwesomeIcon icon={faChevronRight} className="text-xs text-muted" /> : null}
      </a>
    );
  }

  return (
    <button type="button" onPointerEnter={onPointerEnter} onFocus={onPointerEnter} onClick={onSelect} className={className}>
      {body}
      {isSelected ? <FontAwesomeIcon icon={faChevronRight} className="text-xs text-muted" /> : null}
    </button>
  );
}
